import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function BillingThankYou() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [planName, setPlanName] = useState("");

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    let stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/billing/stripe/status/${sessionId}`);
        setPlanName(r.data.plan_name);
        if (r.data.payment_status === "paid") { setStatus("paid"); checkAuth?.(); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
      } catch { /* keep polling */ }
      tries += 1;
      if (tries >= 15) { setStatus("timeout"); return; }
      if (!stop) setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const ok = status === "paid";
  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6">
      <div data-testid="billing-thank-you" className="max-w-md w-full text-center bg-card border border-gray-800 rounded-2xl p-10">
        {status === "checking" ? <Loader2 className="h-12 w-12 mx-auto text-amber-400 animate-spin" />
          : ok ? <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-400" /> : <XCircle className="h-12 w-12 mx-auto text-red-400" />}
        <h1 data-testid="billing-thank-you-title" className="mt-5 font-display text-2xl font-bold">
          {status === "checking" ? "Confirming your payment…" : ok ? "You're subscribed!" : status === "expired" ? "Checkout expired" : "We couldn't confirm the payment yet"}
        </h1>
        <p className="mt-3 text-sm text-gray-400">
          {ok ? `Thank you! ${planName ? `Your ${planName} plan` : "Your plan"} is active and the full portal is unlocked.`
            : status === "checking" ? "This only takes a moment."
            : "If you were charged, your plan will activate within a few minutes. Otherwise please try again from Billing."}
        </p>
        {status !== "checking" && (
          <button data-testid="billing-thank-you-continue" onClick={() => navigate(ok ? "/streams" : "/billing")} className="mt-8 gold-gradient text-black font-semibold px-5 py-2.5 rounded-xl inline-flex items-center gap-2">
            {ok ? "Go to my streams" : "Back to billing"} <ArrowRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
